import { AppContextService } from '../../../services/app-context.service';
import { ActivatedRoute } from '@angular/router';
import { ElementRef, Optional } from '@angular/core';
import { contextIds, viewContextKey, viewContextVisible } from '../../view-context';
import { DataListLoadMoreComponent } from './data-list-load-more.component';
import { DataListCardComponent } from './data-list-card.component';
import { CommonModule } from '@angular/common';
import { DataSelectionBinding, DataViewSelection } from '../../../models/data-selection';
import {
  AfterViewChecked, booleanAttribute, Component, ContentChild, EventEmitter, Input, OnChanges,
  OnDestroy, OnInit, Output, SimpleChanges, TemplateRef, ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { forkJoin, Subject, Subscription, take } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { DataFetch, DataQuery } from '../../../models/data-source';
import { CacheService } from '../../../services/cache.service';
import { DataFilterValues } from '../data-filter/data-filter.component';
import { DataListDetailDirective, DataListItemDirective } from './data-list-item.directive';
import { BrIconComponent } from '../icon/icon.component';
import { BrMenuComponent } from '../menu/menu.component';
import { BrMenuItemComponent } from '../menu/menu-item.component';
import { BrMenuTriggerDirective } from '../menu/menu-trigger.directive';

/** Page-owned choices use that page's existing API sort keys. */
export interface DataListSortOption {
  readonly label: string;
  readonly field: string;
  readonly desc?: boolean;
}

@Component({
  selector: 'app-data-list',
  standalone: true,
  imports: [DataListLoadMoreComponent, DataListCardComponent, CommonModule, ReactiveFormsModule,
    BrIconComponent, BrMenuComponent, BrMenuItemComponent, BrMenuTriggerDirective],
  templateUrl: './data-list.component.html',
  styleUrls: ['./data-list.component.css'],
})
export class DataListComponent<T = unknown> implements OnInit, OnChanges, AfterViewChecked, OnDestroy {
  @Input() contextKey = '';
  @Input({ required: true }) fetch!: DataFetch<T>;
  @Input() filters: DataFilterValues = {};
  @Input() searchable = true;
  /** Controls the visual loading label; fetching state and aria-busy stay active. */
  @Input({ transform: booleanAttribute }) showLoading = true;
  @Input({ transform: booleanAttribute }) autoLoadMore = true;
  @Input({ transform: booleanAttribute }) selectable = false;
  /** Open the first item's detail when a fresh first page arrives. */
  @Input({ transform: booleanAttribute }) expandFirst = false;
  @Output() selectionChange = new EventEmitter<T[]>();
  @Input() searchPlaceholder = 'Search…';
  @Input() search = '';
  @Output() searchChange = new EventEmitter<string>();
  @Input() pageSize = 25;
  @Input() sortOptions: readonly DataListSortOption[] = [];
  selectedSort: DataListSortOption | null = null;
  @Input() emptyText = 'Nothing here yet.';
  @Input() layout: 'list' | 'grid' = 'list';
  @ContentChild('listEmpty') emptyTemplate?: TemplateRef<unknown>;
  @Input() trackByFn: (index: number, item: T) => unknown = (_index, item) => item;
  /** Replacement only: appending never dismisses the current inspected item. */
  @Output() queryChange = new EventEmitter<DataQuery>();
  @Output() loadError = new EventEmitter<unknown>();
  @ContentChild(DataListItemDirective) itemDef?: DataListItemDirective<T>;
  @ContentChild(DataListDetailDirective) detailDef?: DataListDetailDirective<T>;
  @ViewChild('scrollBox') scrollBox?: { nativeElement: HTMLElement };

  readonly searchCtrl = new FormControl('', { nonNullable: true });
  readonly selection = new DataViewSelection<T>(
    () => this.items,
    row => this.trackByFn(0, row),
  );
  @Input() set selectionBinding(binding: DataSelectionBinding | null) { this.selection.binding = binding; }
  get selectionBinding(): DataSelectionBinding | null { return this.selection.binding; }
  items: T[] = [];
  total = 0;
  loading = false;
  errorMessage = '';
  expandedItem: T | null = null;

  // -- Finding one record, on somebody else's instruction ---------------
  //
  // The map beside this list opens a popover on a pin, and the record in
  // it can be clicked. The page turns that into a matcher and hands it
  // here; nothing in this component knows what a lead or a customer is.

  /** The row that was asked for, marked until another one is. */
  revealedItem: T | null = null;

  /** Asked for a record that is in no page of this list. */
  @Output() revealMissed = new EventEmitter<void>();

  private pendingReveal: ((item: T) => boolean) | null = null;
  private revealScrollPending = false;
  private contextRegistration?: Subscription;
  private contextTimer?: ReturnType<typeof setTimeout>;
  private contextRestorePending = false;
  private restoredView?: { version: number | undefined; pages: number; ids: (string | number)[]; expanded: unknown; scroll: number };
  private restoreScroll: number | null = null;
  private currentQuery: DataQuery | null = null;
  private page = 0;
  private failedPage: number | null = null;
  private refreshFailed = false;
  private ready = false;
  private destroyed = false;
  private readonly destroyed$ = new Subject<void>();
  private request?: Subscription;

  constructor(cache: CacheService,
    @Optional() private readonly context?: AppContextService,
    @Optional() private readonly contextRoute?: ActivatedRoute,
    @Optional() private readonly contextElement?: ElementRef<HTMLElement>,
  ) {
    this.selection.changed.pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.selectionChange.emit([...this.selection.selected]));
    cache.sessionChanged$.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      clearTimeout(this.contextTimer);
      this.restoredView = undefined;
      this.restoreScroll = null;
      this.request?.unsubscribe();
      this.items = [];
      this.selection.resetLocal();
      this.selection.rowsChanged();
      this.expandedItem = null;
      this.total = 0;
      this.page = 0;
      this.selectedSort = null;
      this.failedPage = null;
      this.errorMessage = '';
      this.loading = false;
    });
  }

  get hasMore(): boolean { return this.items.length < this.total; }

  /**
   * Find one record and bring it into view.
   *
   * The matcher is kept rather than run once: the record somebody clicked
   * on the map is usually not on the first page, and a list that answered
   * "not here" while it still had pages to read would be answering about
   * itself rather than about the record. So it reads on, page by page,
   * the way scrolling would - and when the pages run out it says so, once.
   *
   * It survives a reload for the same reason: a page that widens its
   * filters and reloads is still looking for the same record.
   */
  reveal(match: (item: T) => boolean): void {
    this.pendingReveal = match;
    this.tryReveal();
  }

  private tryReveal(): void {
    const match = this.pendingReveal;
    if (!match) return;
    const found = this.items.find(match);
    if (found) {
      this.pendingReveal = null;
      this.revealedItem = found;
      // The row may not be drawn yet; the scroll happens once it is.
      this.revealScrollPending = true;
      return;
    }
    // A page already on its way will try again when it lands, so asking
    // for another one here would read the same page twice.
    if (this.loading || this.errorMessage) return;
    if (this.hasMore) { this.loadMore(); return; }
    this.pendingReveal = null;
    this.revealMissed.emit();
  }
  get allLoadedSelected(): boolean { return this.items.length > 0 && this.selection.selected.length === this.items.length; }

  toggleAllLoaded(): void {
    if (!this.selectable) return;
    if (this.allLoadedSelected) this.selection.clear();
    else this.selection.select(...this.items);
  }

  ngOnInit(): void {
    this.searchCtrl.setValue(this.search, { emitEvent: false });
    this.contextRegistration = this.context?.registerView({
      key: viewContextKey(this.contextRoute, `list:${this.contextKey || this.searchPlaceholder}`),
      name: 'Visible list', priority: 10,
      active: () => viewContextVisible(this.contextElement),
      read: () => ({ query: this.currentQuery, loading: this.loading, error: this.errorMessage,
        total: this.total, pages: this.page, selectionOwner: this.selectionBinding?.key ?? null,
        selectedIds: this.selectionBinding ? [] : contextIds(this.selection.selected.map(item => this.trackByFn(0, item))),
        expanded: this.expandedItem ? contextIds([this.trackByFn(0, this.expandedItem)])[0] ?? null : null,
        scroll: this.scrollBox?.nativeElement.scrollTop ?? 0 }),
      describe: state => JSON.stringify({ query: state.query, loading: state.loading,
        error: state.error, total: state.total, ...(state.selectionOwner ? { selectionOwner: state.selectionOwner }
          : { selectedIds: state.selectedIds.slice(0, 200), selectedCount: state.selectedIds.length }), expandedId: state.expanded }),
      restore: state => {
        if (!state.query) return;
        const version = this.context?.restorationVersion;
        this.contextRestorePending = true;
        clearTimeout(this.contextTimer);
        this.contextTimer = setTimeout(() => {
          this.contextRestorePending = false;
          if (this.destroyed || !state.query || version !== this.context?.restorationVersion) return;
          this.searchCtrl.setValue(state.query.search, { emitEvent: false });
          this.searchChange.emit(state.query.search);
          this.selectedSort = this.sortOptions.find(option => option.field === state.query?.sort
            && !!option.desc === !!state.query?.desc) ?? null;
          this.pageSize = state.query.pageSize;
          this.restoredView = { version, pages: Math.max(1, state.pages), ids: state.selectedIds,
            expanded: state.expanded, scroll: state.scroll };
          this.reload(true);
        });
      },
    });
    this.searchCtrl.valueChanges.pipe(
      debounceTime(300), distinctUntilChanged(), takeUntil(this.destroyed$),
    ).subscribe(value => {
      if (value !== this.searchCtrl.value) return;
      this.restoredView = undefined;
      this.searchChange.emit(value);
      this.reload();
    });
    Promise.resolve().then(() => {
      if (this.destroyed) return;
      this.ready = true;
      this.reload(this.contextRestorePending);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectable'] && !this.selectable) this.selection.resetLocal();
    const sortRemoved = !!changes['sortOptions'] && !!this.selectedSort && !this.sortOptions.includes(this.selectedSort);
    if (sortRemoved) this.selectedSort = null;
    const searchChanged = !!changes['search'] && this.searchCtrl.value !== this.search;
    if (searchChanged) this.searchCtrl.setValue(this.search, { emitEvent: false });
    if (this.ready && (sortRemoved || searchChanged || ['filters', 'fetch', 'pageSize'].some(key => changes[key]))) this.reload(this.contextRestorePending);
  }

  toggle(item: T): void { this.expandedItem = this.expandedItem === item ? null : item; }

  setSort(option: DataListSortOption | null): void {
    if (option === this.selectedSort || (option && !this.sortOptions.includes(option))) return;
    this.selectedSort = option;
    this.reload();
  }

  reload(restoring = false): void {
    if (this.destroyed) return;
    if (!restoring) {
      this.contextRestorePending = false;
      this.restoredView = undefined;
      clearTimeout(this.contextTimer);
    }
    this.request?.unsubscribe();
    this.items = [];
    this.selection.resetLocal();
    this.selection.rowsChanged();
    this.expandedItem = null;
    // The rows are being replaced, so a mark on one of them is about to
    // mean nothing. A reveal still being looked for survives: it is about
    // a record, not about the rows currently loaded.
    this.revealedItem = null;
    this.total = 0;
    this.page = 0;
    if (this.scrollBox) this.scrollBox.nativeElement.scrollTop = 0;
    this.load(1);
  }

  /** Refresh the loaded range without discarding scroll position or appended pages. */
  refresh(): void {
    if (this.destroyed || this.loading || !this.page) return;
    this.request?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';
    this.refreshFailed = false;
    const selectedKeys = new Set(this.selection.selected.map(item => this.trackByFn(0, item)));
    const expandedKey = this.expandedItem ? this.trackByFn(0, this.expandedItem) : undefined;
    const requests = Array.from({ length: this.page }, (_, index) => this.fetch({
      search: this.searchCtrl.value.trim(), filters: { ...this.filters },
      page: index + 1, pageSize: this.pageSize,
      ...(this.selectedSort ? { sort: this.selectedSort.field, desc: this.selectedSort.desc ?? false } : {}),
    }).pipe(take(1)));
    this.request = forkJoin(requests).pipe(takeUntil(this.destroyed$)).subscribe({
      next: pages => {
        this.items = pages.flatMap(page => page.items);
        this.total = pages[0].total;
        if (!this.selectionBinding && this.selection.hasValue()) {
          this.selection.clear();
          this.selection.select(...this.items.filter(item => selectedKeys.has(this.trackByFn(0, item))));
        }
        this.selection.rowsChanged();
        this.expandedItem = expandedKey === undefined ? null :
          this.items.find(item => this.trackByFn(0, item) === expandedKey) ?? null;
        this.loading = false;
      },
      error: error => {
        this.loading = false;
        this.refreshFailed = true;
        this.errorMessage = 'Unable to refresh items. Try again.';
        this.loadError.emit(error);
      },
    });
  }

  loadMore(): void {
    if (!this.loading && !this.errorMessage && this.hasMore) this.load(this.page + 1);
  }

  retry(): void {
    if (this.refreshFailed) this.refresh();
    else if (!this.loading) this.load(this.failedPage ?? 1);
  }

  private load(page: number): void {
    if (this.destroyed || !this.fetch) return;
    this.request?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';
    this.failedPage = null;
    this.refreshFailed = false;
    const query: DataQuery = {
      search: this.searchCtrl.value.trim(), page, pageSize: this.pageSize,
      filters: { ...this.filters },
      ...(this.selectedSort ? { sort: this.selectedSort.field, desc: this.selectedSort.desc ?? false } : {}),
    };
    this.currentQuery = query;
    if (page === 1) this.queryChange.emit(query);
    this.request = this.fetch(query).pipe(takeUntil(this.destroyed$)).subscribe({
      next: result => {
        this.loading = false;
        if (page > 1 && !result.items.length && this.items.length < result.total) {
          this.failedPage = page;
          this.errorMessage = 'The result set changed. Retry or change the search.';
          return;
        }
        this.items = page === 1 ? result.items : [...this.items, ...result.items];
        this.total = result.total;
        this.page = page;
        if (page === 1 && this.expandFirst && !this.expandedItem) this.expandedItem = this.items[0] ?? null;
        this.selection.rowsChanged();
        // Whatever was being looked for is looked for again in what just
        // arrived - this is what carries a reveal across paging.
        this.tryReveal();
        if (this.restoredView?.version !== this.context?.restorationVersion) this.restoredView = undefined;
        if (this.restoredView) {
          const held = this.restoredView;
          if (page < held.pages && this.hasMore) {
            this.contextTimer = setTimeout(() => {
              if (held.version === this.context?.restorationVersion) this.loadMore();
            });
          } else {
            this.restoredView = undefined;
            if (this.selectable && !this.selectionBinding) this.selection.select(...this.items.filter(item => held.ids.includes(this.trackByFn(0, item) as string | number)));
            this.expandedItem = this.items.find(item => this.trackByFn(0, item) === held.expanded) ?? null;
            this.restoreScroll = held.scroll;
          }
        }
      },
      error: error => {
        this.loading = false;
        this.failedPage = page;
        this.errorMessage = page === 1 ? 'Unable to load items. Try again.' : 'Unable to load more items. Try again.';
        this.loadError.emit(error);
      },
    });
  }

  ngAfterViewChecked(): void {
    if (this.restoreScroll !== null && this.scrollBox && !this.loading) {
      this.scrollBox.nativeElement.scrollTop = this.restoreScroll;
      this.restoreScroll = null;
    }
    this.scrollRevealedIntoView();
  }

  /**
   * Put the asked-for row on screen, once it has been drawn.
   *
   * `nearest` rather than `center`: a row already in view must not jump,
   * and a list scrolled far from it should move no further than it has to.
   */
  private scrollRevealedIntoView(): void {
    if (!this.revealScrollPending || !this.revealedItem) return;
    const index = this.items.indexOf(this.revealedItem);
    if (index < 0) return;
    const row = this.scrollBox?.nativeElement
      .querySelectorAll<HTMLElement>('.dl-item')[index];
    if (!row) return;
    this.revealScrollPending = false;
    row.scrollIntoView({ block: 'nearest' });
  }

  ngOnDestroy(): void {
    this.contextRegistration?.unsubscribe();
    clearTimeout(this.contextTimer);
    this.selection.destroy();
    this.destroyed = true;
    this.request?.unsubscribe();
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
